import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Loader2, CheckCircle2, Phone, User as UserIcon, Youtube } from 'lucide-react';

const PublicLeadForm = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    api.getPublicLeadForm(slug)
      .then((res) => setForm(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (name.trim().length < 2) {
      setError('Please enter your full name');
      return;
    }
    const cleanPhone = phone.replace(/[^0-9+]/g, '');
    if (cleanPhone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    setSubmitting(true);
    try {
      await api.submitPublicLeadForm(slug, { name: name.trim(), phone: cleanPhone });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (notFound) {
    // Fall back to app routing if this slug isn't a lead-form (in case user typed a known app path)
    return <Navigate to="/login" replace />;
  }

  const color = form.primary_color || '#0891b2';

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{
        background: `linear-gradient(135deg, ${color} 0%, ${color}dd 50%, #0f172a 100%)`,
      }}
    >
      <div data-testid="public-lead-form" className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Poster banner */}
        {form.poster_url ? (
          <div className="relative">
            <img
              src={form.poster_url}
              alt={form.batch_name}
              className="w-full h-48 sm:h-56 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4 text-white">
              <div className="text-xs uppercase tracking-wider opacity-90">{form.batch_name}</div>
              <div className="text-xl font-bold leading-tight drop-shadow">{form.headline || form.batch_name}</div>
            </div>
          </div>
        ) : (
          <div
            className="py-10 px-6 text-white text-center"
            style={{ backgroundColor: color }}
          >
            <div className="text-xs uppercase tracking-wider opacity-90 mb-2">{form.batch_name}</div>
            <h1 className="text-2xl sm:text-3xl font-bold leading-tight">
              {form.headline || form.batch_name}
            </h1>
          </div>
        )}

        {/* Body */}
        <div className="p-6 sm:p-8">
          {form.sub_headline && (
            <p className="text-slate-600 text-sm mb-6 leading-relaxed text-center">
              {form.sub_headline}
            </p>
          )}

          {success ? (
            <div className="text-center py-8" data-testid="form-success">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: `${color}22` }}
              >
                <CheckCircle2 size={36} style={{ color }} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">You&apos;re all set!</h2>
              <p className="text-slate-600 text-sm">
                Our team will reach out to you shortly. Keep your phone handy.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Your Name
                </label>
                <div className="relative">
                  <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    data-testid="public-name-input"
                    className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': color }}
                    required
                    autoComplete="name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="10-digit mobile number"
                    data-testid="public-phone-input"
                    className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                    style={{ '--tw-ring-color': color }}
                    required
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="public-submit-btn"
                className="w-full py-3.5 rounded-lg text-white font-semibold text-base shadow-md hover:opacity-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ backgroundColor: color }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Submitting…
                  </>
                ) : (
                  form.cta_text || 'Get a Call Back'
                )}
              </button>

              <p className="text-xs text-slate-400 text-center mt-2">
                By submitting, you agree to receive a call from our team about this batch.
              </p>
            </form>
          )}

          {form.youtube_link && (
            <a
              href={form.youtube_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900"
            >
              <Youtube size={16} />
              Watch on YouTube
            </a>
          )}
        </div>

        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-center text-xs text-slate-400">
          Exampur · Trusted by lakhs of aspirants
        </div>
      </div>
    </div>
  );
};

export default PublicLeadForm;
